import React, { useEffect, useRef, useState } from "react";

import { CropDataCompleteness, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { CROP_TYPE_FCPO, FieldCropRecommendation } from "models";

import { Card, FieldManagementCropCard, Flex, Icon, LoadingOverlay, Popover, TooltipWrapper } from "components";

import { type CropDetailsLine } from "pages/app/all-field-view/utils/CropDetailsUtils";
import { CropRecommendationCardForm } from "pages/app/field-timeline/components/CropRecommendationCardForm";
import { FieldBaselineYearRange } from "pages/app/field-timeline/utils/PracticeUtils";

export type PracticeBundleOpts = {
  hasMissingData: boolean;
  slug: string;
};

export type CropRotationCardProps = {
  activePracticeBundles?: Array<PracticeBundleOpts>;
  baselineYearRange?: FieldBaselineYearRange | null;
  cropData?: CropDataCompleteness;
  cropDetailsLines?: CropDetailsLine[];
  cropId: string;
  cropMixId?: string;
  cropName?: string;
  cropType: CROP_TYPE_FCPO;
  currency?: CurrencyCode;
  dataTestId?: string;
  enrolledInInventoryProgram?: boolean;
  fieldId: string;
  fieldSize?: number;
  hasResidue?: boolean;
  isBaseline?: boolean;
  isConfirmed?: boolean;
  isEmptyCard?: boolean;
  isEmptyWithNoEnrollment?: boolean;
  isFlaggedByVerifier?: boolean;
  isMandatory?: boolean;
  isReportingYear?: boolean;
  isSelected?: boolean;
  openTasksCount?: number;
  recommendations?: {
    fieldId: string;
    harvestEndDate: string;
    seedingStartDate: string;
    cropRecommendations: FieldCropRecommendation[];
  };
  year?: number;
  onClick?: () => void;
  // Multi-selection props
  isChecked?: boolean;
  isInactive?: boolean;
  // TODO (Alp): Performance optimization: Pass fetching state from parent instead of subscribing in every card
  isFetching?: boolean;
};

export const CropRotationRecommendationCard = ({
  className,
  cropType,
  dataTestId,
  fieldSize,
  isInactive = false,
  isSelected,
  recommendations,
  year,
}: {
  className?: string;
  cropType: CROP_TYPE_FCPO;
  currency?: CurrencyCode;
  dataTestId?: string;
  fieldSize?: number;
  isInactive?: boolean;
  isSelected?: boolean;
  recommendations?: {
    fieldId: string;
    harvestEndDate: string;
    seedingStartDate: string;
    cropRecommendations: FieldCropRecommendation[];
  };
  year?: number;
}) => {
  const { t } = useTranslation("fieldTimeline");
  const [shouldShowTextTooltip, setShouldShowTextTooltip] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const nameContainerRef = useRef<HTMLParagraphElement | null>(null);
  const nameRef = useRef<HTMLParagraphElement | null>(null);

  const isCoverCrop = cropType === CROP_TYPE_FCPO.COVER;

  useEffect(() => {
    const nameContainerElement = nameContainerRef.current;
    const nameElement = nameRef.current;
    if (!nameContainerElement || !nameElement) return;

    const handleResize = () => {
      setShouldShowTextTooltip(nameContainerElement!.offsetWidth < nameElement!.offsetWidth);
    };

    const observer = new ResizeObserver(handleResize);

    const cardElements = [nameContainerElement, nameElement];
    // start the observer of each card ref
    cardElements.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <Popover
      className="crop-recommendation-card-popover"
      content={
        recommendations && (
          <CropRecommendationCardForm
            harvestEndDate={recommendations.harvestEndDate}
            seedingStartDate={recommendations.seedingStartDate}
            cropType={cropType}
            recommendations={recommendations.cropRecommendations}
            year={year}
            fieldId={recommendations.fieldId}
            fieldSize={fieldSize}
            onClose={() => setShowModal(false)}
          />
        )
      }
      trigger={isInactive ? undefined : "click"}
      placement="left"
      open={isInactive || isCoverCrop ? false : showModal}
      onOpenChange={!isInactive ? () => setShowModal(!showModal) : undefined}
      destroyOnHidden={true}
    >
      <Card
        className={`recommendation-crop-empty-card layout ${isSelected ? "selected" : isInactive ? "inactive-card" : ""} ${
          isCoverCrop ? "cover-crop" : "undersowing-crop"
        } ${className ?? ""}`}
        onClick={!isInactive ? () => setShowModal(!showModal) : undefined}
        data-testid={`side-crop-empty-card-${dataTestId}`}
      >
        <TooltipWrapper
          // avoid having the tooltip open when the modal is open
          open={showModal ? false : undefined}
          tooltip={
            isCoverCrop
              ? {
                  title: t("cropCard.cmcRecommendationCoverCropTooltip"),
                  placement: "top",
                }
              : shouldShowTextTooltip
              ? {
                  title: `${t(isCoverCrop ? "cropCard.recommendationCoverCrop" : "cropCard.recommendationInterseeding")}`,
                  placement: "top",
                }
              : {}
          }
        >
          <Flex className="payout-content">
            <Flex className="icon-container">
              <Icon
                dataTestId={`side-crop-empty-card-${dataTestId}-bulb-icon ${isCoverCrop ? "cover-crop" : ""}`}
                className="recommendation-card-icon"
                iconName={isCoverCrop ? "klim/plant-ai" : "tabler/bulb"}
              />
            </Flex>
            <div ref={nameContainerRef} className="paragraph-m payout-text-container">
              <span ref={nameRef} className="payout-text">
                {`${
                  isCoverCrop
                    ? t("cropCard.cmcRecommendationCoverCrop")
                    : `${t(isCoverCrop ? "cropCard.recommendationCoverCrop" : "cropCard.recommendationInterseeding")}`
                }`}
              </span>
            </div>
          </Flex>
        </TooltipWrapper>
      </Card>
    </Popover>
  );
};

export const CropRotationCard = React.memo(
  ({
    activePracticeBundles = [],
    baselineYearRange,
    cropDetailsLines,
    cropId,
    cropMixId,
    cropName,
    cropType,
    currency,
    dataTestId,
    enrolledInInventoryProgram = false,
    fieldId,
    fieldSize,
    hasResidue,
    isBaseline,
    isChecked = false,
    isConfirmed,
    isEmptyCard,
    isEmptyWithNoEnrollment = false,
    isFetching = false,
    isFlaggedByVerifier = false,
    isInactive = false,
    isMandatory,
    isReportingYear = false,
    isSelected = false,
    openTasksCount,
    recommendations,
    year,
    onClick,
  }: CropRotationCardProps) => {
    const [shouldShowNameTooltip, setShouldShowCropNameTooltip] = useState<boolean>(false);
    const [shouldHideIcons, setShouldHideIcons] = useState<boolean>(false);
    const [shouldHideBaselineTag, setShouldHideBaselineTag] = useState(false);
    const [isNarrow, setIsNarrow] = useState(false);

    const cardRef = useRef<HTMLDivElement | null>(null);
    const nameContainerRef = useRef<HTMLParagraphElement | null>(null);
    const nameRef = useRef<HTMLParagraphElement | null>(null);

    useEffect(() => {
      const nameContainerElement = nameContainerRef.current;
      const nameElement = nameRef.current;
      const cardElement = cardRef.current;
      if (!nameContainerElement || !nameElement || !cardElement || !cardElement) return;

      const handleResize = () => {
        setShouldShowCropNameTooltip(nameElement!.scrollWidth > nameElement!.clientWidth);
        setShouldHideBaselineTag(cardElement.offsetWidth < 175);
        setShouldHideIcons(cardElement.offsetWidth < 80);
        setIsNarrow(cardElement.offsetWidth < 80);
      };

      const observer = new ResizeObserver(handleResize);

      const cardElements = [cardElement, nameContainerElement, nameElement];
      // start the observer of each card ref
      cardElements.forEach((element) => {
        if (element) observer.observe(element);
      });

      return () => observer.disconnect();
    }, []);

    const Card = (
      <FieldManagementCropCard
        activePracticeBundles={activePracticeBundles}
        baselineYearRange={baselineYearRange}
        cropDetailsLines={cropDetailsLines}
        cropId={cropId}
        cropMixId={cropMixId}
        cropName={cropName}
        cropType={cropType}
        currency={currency}
        dataTestId={dataTestId}
        enrolledInInventoryProgram={enrolledInInventoryProgram}
        fieldId={fieldId}
        fieldSize={fieldSize}
        hasResidue={hasResidue}
        isBaseline={isBaseline}
        isChecked={isChecked}
        isConfirmed={isConfirmed}
        isEmptyCard={isEmptyCard}
        isEmptyWithNoEnrollment={isEmptyWithNoEnrollment}
        isFetching={isFetching}
        isFlaggedByVerifier={isFlaggedByVerifier}
        isInactive={isInactive}
        isMandatory={isMandatory}
        isNarrow={isNarrow}
        isReportingYear={isReportingYear}
        isSelected={isSelected}
        nameContainerRef={nameContainerRef}
        nameRef={nameRef}
        openTasksCount={openTasksCount}
        recommendations={recommendations}
        shouldHideBaselineTag={shouldHideBaselineTag}
        shouldHideIcons={shouldHideIcons}
        shouldShowNameTooltip={shouldShowNameTooltip}
        year={year}
        onClick={onClick}
      />
    );

    return <div ref={cardRef}>{isFetching ? <LoadingOverlay>{Card}</LoadingOverlay> : Card}</div>;
  }
);
